<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { useAnalytics } from '@/shared/analytics'
import { useBradleyEnvironment } from '@/shared/composables/use-bradley-environment.js'
import { useDayJs } from '@/shared/composables/use-dayjs'
import {
  BRADLEY_ANALYSE_DATA_TOOL_LABEL,
  BRADLEY_ANALYTICS_EVENTS,
} from '../analytics/bradley-analytics.constants.js'
import { formatBradleyFileSize } from '../utils/bradley-attachment.util.js'

const props = defineProps({
  projectId: {
    type: String,
    default: null,
  },
  projectTitle: {
    type: String,
    default: '',
  },
  conversationId: {
    type: [String, Number],
    default: null,
  },
  projectFiles: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['addFile', 'selectTool', 'reuseProjectFiles'])

const { t, locale } = useI18n()
const { trackEvent } = useAnalytics()
const authStore = useAuthStore()
const dayJs = useDayJs()
const {
  documentUploadEnabled: DOCUMENT_UPLOAD_ENABLED,
  dataAnalysisEnabled: DATA_ANALYSIS_ENABLED,
  newFileUploadEnabled: NEW_FILE_UPLOAD_ENABLED,
  projectsEnabled: PROJECTS_ENABLED,
} = useBradleyEnvironment()

const projectFilesUtil = shallowRef(null)
const projectAnalytics = shallowRef(null)
if (PROJECTS_ENABLED) {
  import('../utils/bradley-project-files.util.js').then((module) => {
    projectFilesUtil.value = module
  })
  import('../analytics/bradley-project-analytics.telemetry.js').then((module) => {
    projectAnalytics.value = module
  })
}

function getBradleyProjectFileId(file) {
  return projectFilesUtil.value?.getBradleyProjectFileId?.(file) || ''
}
function getBradleyProjectFileName(file) {
  return projectFilesUtil.value?.getBradleyProjectFileName?.(file) || ''
}
function getBradleyProjectFileSizeBytes(file) {
  return projectFilesUtil.value?.getBradleyProjectFileSizeBytes?.(file) || 0
}
function getBradleyProjectFileUpdatedAt(file) {
  return projectFilesUtil.value?.getBradleyProjectFileUpdatedAt?.(file) || null
}
function getBradleyProjectFileIconClass(file) {
  return projectFilesUtil.value?.getBradleyProjectFileIconClass?.(file) || 'pi pi-file'
}

const menuOpen = ref(false)
const view = ref('menu')
const selectedFileIds = ref([])
const rootReference = ref(null)
const triggerReference = ref(null)
const panelReference = ref(null)
const panelStyle = ref({})
const openUpward = ref(true)
let ignoreOutsideClickUntil = 0

const MENU_WIDTH = 328
const PICKER_WIDTH = 352
const VIEWPORT_PADDING = 12
const GAP = 10

const hasMenuActions = computed(() => DOCUMENT_UPLOAD_ENABLED || DATA_ANALYSIS_ENABLED)
const isProjectContext = computed(() => Boolean(props.projectId))
const addFileLabelKey = computed(() => (
  NEW_FILE_UPLOAD_ENABLED ? 'newBradley.input.addFile' : 'newBradley.input.addFileDocuments'
))

const sortedProjectFiles = computed(() => {
  const sort = projectFilesUtil.value?.sortBradleyProjectFilesByUpdatedAt
  return sort ? sort(props.projectFiles || []) : [...(props.projectFiles || [])]
})

const showProjectFilesOption = computed(() => (
  isProjectContext.value && DOCUMENT_UPLOAD_ENABLED
))

const displayProjectTitle = computed(() => {
  const trimmed = typeof props.projectTitle === 'string' ? props.projectTitle.trim() : ''
  return trimmed || t('newBradley.sidebar.unnamedProject')
})

const projectTitleUpper = computed(() => displayProjectTitle.value.toUpperCase())

const selectedCount = computed(() => selectedFileIds.value.length)

const attachLabel = computed(() => {
  const count = selectedCount.value
  if (count === 1) {
    return t('newBradley.projectWorkspace.files.attachOneFile')
  }
  return t('newBradley.projectWorkspace.files.attachFiles', { count })
})

const projectFilesCountLabel = computed(() => {
  const count = sortedProjectFiles.value.length
  if (count === 1) {
    return t('newBradley.projectWorkspace.files.filesCountOne')
  }
  return t('newBradley.projectWorkspace.files.filesCount', { count })
})

const panelWidth = computed(() => (
  view.value === 'project-files' ? PICKER_WIDTH : MENU_WIDTH
))

function trackProjectPromptEvent(eventName, { action, toolName = null, status = null } = {}) {
  if (!props.projectId) {
    return
  }

  const buildPayload = projectAnalytics.value?.bradleyProjectPromptAnalyticsPayload
  if (!buildPayload) {
    return
  }

  trackEvent(eventName, buildPayload({
    action,
    projectId: props.projectId,
    chatId: props.conversationId,
    userId: authStore?.userData?.id ?? null,
    toolName,
    triggerSource: 'project_composer',
    status,
  }))
}

function suppressNextOutsideClick() {
  ignoreOutsideClickUntil = Date.now() + 250
}

function resetPickerState() {
  view.value = 'menu'
  selectedFileIds.value = []
}

function updatePanelPosition() {
  const trigger = triggerReference.value
  if (!trigger) {
    return
  }

  const rect = trigger.getBoundingClientRect()
  const width = panelWidth.value
  const estimatedHeight = view.value === 'project-files' ? 360 : 220
  const spaceAbove = rect.top - VIEWPORT_PADDING
  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING

  openUpward.value = spaceAbove >= estimatedHeight || spaceAbove >= spaceBelow

  let left = rect.left
  left = Math.min(left, window.innerWidth - width - VIEWPORT_PADDING)
  left = Math.max(VIEWPORT_PADDING, left)

  if (openUpward.value) {
    const bottom = window.innerHeight - rect.top + GAP
    panelStyle.value = {
      position: 'fixed',
      left: `${left}px`,
      bottom: `${bottom}px`,
      top: 'auto',
      width: `${width}px`,
      zIndex: 200,
    }
  }
  else {
    const top = rect.bottom + GAP
    panelStyle.value = {
      position: 'fixed',
      left: `${left}px`,
      top: `${top}px`,
      bottom: 'auto',
      width: `${width}px`,
      zIndex: 200,
    }
  }
}

async function openMenu() {
  suppressNextOutsideClick()
  resetPickerState()
  menuOpen.value = true
  trackProjectPromptEvent(BRADLEY_ANALYTICS_EVENTS.PROJECT_PROMPT_TOOL_BUTTON_CLICK, {
    action: 'tool_button_click',
  })
  if (isProjectContext.value) {
    await nextTick()
    updatePanelPosition()
  }
}

function closeMenu() {
  menuOpen.value = false
  resetPickerState()
}

function toggleMenu(event) {
  event?.stopPropagation?.()
  if (menuOpen.value) {
    closeMenu()
    return
  }
  openMenu()
}

function onAddFile(event) {
  event?.stopPropagation?.()
  emit('addFile')
  closeMenu()
}

function onAnalyzeData(event) {
  event?.stopPropagation?.()
  trackProjectPromptEvent(BRADLEY_ANALYTICS_EVENTS.PROJECT_PROMPT_TOOL_SELECTED, {
    action: 'tool_selected',
    toolName: BRADLEY_ANALYSE_DATA_TOOL_LABEL,
    status: 'selected',
  })
  emit('selectTool', BRADLEY_ANALYSE_DATA_TOOL_LABEL)
  closeMenu()
}

async function openProjectFilesPicker(event) {
  event?.stopPropagation?.()
  suppressNextOutsideClick()
  view.value = 'project-files'
  selectedFileIds.value = []
  await nextTick()
  updatePanelPosition()
}

async function backToMenu(event) {
  event?.stopPropagation?.()
  suppressNextOutsideClick()
  view.value = 'menu'
  selectedFileIds.value = []
  await nextTick()
  updatePanelPosition()
}

function isFileSelected(file) {
  const fileId = getBradleyProjectFileId(file)
  return selectedFileIds.value.includes(fileId)
}

function toggleFileSelection(file, event) {
  event?.stopPropagation?.()
  const fileId = getBradleyProjectFileId(file)
  if (!fileId) {
    return
  }

  if (selectedFileIds.value.includes(fileId)) {
    selectedFileIds.value = selectedFileIds.value.filter(id => id !== fileId)
    return
  }

  selectedFileIds.value = [...selectedFileIds.value, fileId]
}

function formatUploadedLabel(file) {
  const updatedAt = getBradleyProjectFileUpdatedAt(file)
  if (!updatedAt) {
    return formatBradleyFileSize(getBradleyProjectFileSizeBytes(file))
  }

  return t('newBradley.projectWorkspace.files.uploadedMeta', {
    size: formatBradleyFileSize(getBradleyProjectFileSizeBytes(file)),
    when: dayJs.timeElapsed(updatedAt, locale.value),
  })
}

function attachSelectedFiles(event) {
  event?.stopPropagation?.()
  if (!selectedCount.value) {
    return
  }

  const selectedFiles = sortedProjectFiles.value.filter(
    file => selectedFileIds.value.includes(getBradleyProjectFileId(file)),
  )

  emit('reuseProjectFiles', selectedFiles)
  closeMenu()
}

function isEventInsideMenu(event) {
  const target = event.target
  if (!(target instanceof Node)) {
    return false
  }
  if (rootReference.value?.contains(target)) {
    return true
  }
  if (panelReference.value?.contains(target)) {
    return true
  }
  return false
}

function handleOutsideClick(event) {
  if (!menuOpen.value) {
    return
  }
  if (Date.now() < ignoreOutsideClickUntil) {
    return
  }
  if (isEventInsideMenu(event)) {
    return
  }
  closeMenu()
}

function handleViewportChange() {
  if (!menuOpen.value || !isProjectContext.value) {
    return
  }
  updatePanelPosition()
}

watch(
  () => props.projectId,
  () => {
    if (menuOpen.value) {
      closeMenu()
    }
    else {
      resetPickerState()
    }
  },
)

watch(view, async () => {
  if (!menuOpen.value || !isProjectContext.value) {
    return
  }
  await nextTick()
  updatePanelPosition()
})

onMounted(() => {
  document.addEventListener('click', handleOutsideClick, true)
  window.addEventListener('resize', handleViewportChange)
  window.addEventListener('scroll', handleViewportChange, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick, true)
  window.removeEventListener('resize', handleViewportChange)
  window.removeEventListener('scroll', handleViewportChange, true)
})

defineExpose({
  closeMenu,
})
</script>

<template>
  <div
    v-if="hasMenuActions"
    ref="rootReference"
    class="bradley-plus-menu"
    :class="{ 'bradley-plus-menu--legacy': !isProjectContext }"
  >
    <button
      ref="triggerReference"
      type="button"
      class="bradley-plus-menu__trigger"
      :aria-label="t('newBradley.input.addMenuAria')"
      :aria-expanded="menuOpen"
      @click="toggleMenu"
    >
      <i
        class="pi pi-plus"
        aria-hidden="true"
      />
    </button>

    <!-- Default (non-project) tooltip — keep original simple style -->
    <transition
      v-if="!isProjectContext"
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="menuOpen"
        ref="panelReference"
        class="bradley-plus-menu__legacy-panel"
        role="menu"
        @click.stop
      >
        <button
          v-if="DOCUMENT_UPLOAD_ENABLED"
          type="button"
          class="bradley-plus-menu__legacy-item"
          role="menuitem"
          @click="onAddFile"
        >
          <i class="pi pi-upload bradley-plus-menu__legacy-icon" />
          <span>{{ t(addFileLabelKey) }}</span>
        </button>
        <button
          v-if="DATA_ANALYSIS_ENABLED"
          type="button"
          class="bradley-plus-menu__legacy-item"
          role="menuitem"
          @click="onAnalyzeData"
        >
          <i class="pi pi-chart-bar bradley-plus-menu__legacy-icon" />
          <span>{{ t('newBradley.tools.analyzeData') }}</span>
        </button>
      </div>
    </transition>

    <!-- Project tooltip — rich style with file reuse -->
    <teleport
      v-if="isProjectContext"
      to="body"
    >
      <transition
        enter-active-class="bradley-plus-menu__transition-enter-active"
        enter-from-class="bradley-plus-menu__transition-enter-from"
        enter-to-class="bradley-plus-menu__transition-enter-to"
        leave-active-class="bradley-plus-menu__transition-leave-active"
        leave-from-class="bradley-plus-menu__transition-leave-from"
        leave-to-class="bradley-plus-menu__transition-leave-to"
      >
        <div
          v-if="menuOpen"
          ref="panelReference"
          class="bradley-plus-menu__panel"
          :class="{
            'bradley-plus-menu__panel--picker': view === 'project-files',
            'bradley-plus-menu__panel--down': !openUpward,
          }"
          :style="panelStyle"
          role="menu"
          @click.stop
        >
          <template v-if="view === 'menu'">
            <button
              v-if="DOCUMENT_UPLOAD_ENABLED"
              type="button"
              class="bradley-plus-menu__item"
              role="menuitem"
              @click="onAddFile"
            >
              <span
                class="bradley-plus-menu__icon"
                aria-hidden="true"
              >
                <i class="pi pi-upload" />
              </span>
              <span class="bradley-plus-menu__copy">
                <span class="bradley-plus-menu__title">
                  {{ t('newBradley.projectWorkspace.files.uploadFromFiles') }}
                </span>
                <span class="bradley-plus-menu__subtitle">
                  {{ t('newBradley.projectWorkspace.files.uploadFromFilesTypes') }}
                </span>
                <span class="bradley-plus-menu__subtitle">
                  {{ t('newBradley.projectWorkspace.files.uploadFromFilesLimit') }}
                </span>
              </span>
            </button>

            <button
              v-if="showProjectFilesOption"
              type="button"
              class="bradley-plus-menu__item"
              role="menuitem"
              @click="openProjectFilesPicker"
            >
              <span
                class="bradley-plus-menu__icon"
                aria-hidden="true"
              >
                <i class="pi pi-folder" />
              </span>
              <span class="bradley-plus-menu__copy bradley-plus-menu__copy--grow">
                <span class="bradley-plus-menu__title">
                  {{ t('newBradley.projectWorkspace.files.selectFromProject', { project: displayProjectTitle }) }}
                </span>
                <span class="bradley-plus-menu__subtitle">
                  {{ projectFilesCountLabel }}
                </span>
              </span>
              <i
                class="pi pi-chevron-right bradley-plus-menu__chevron"
                aria-hidden="true"
              />
            </button>

            <div
              v-if="DOCUMENT_UPLOAD_ENABLED && DATA_ANALYSIS_ENABLED"
              class="bradley-plus-menu__divider"
            />

            <button
              v-if="DATA_ANALYSIS_ENABLED"
              type="button"
              class="bradley-plus-menu__item"
              role="menuitem"
              @click="onAnalyzeData"
            >
              <span
                class="bradley-plus-menu__icon bradley-plus-menu__icon--analyse"
                aria-hidden="true"
              >
                <i class="pi pi-sparkles" />
              </span>
              <span class="bradley-plus-menu__copy">
                <span class="bradley-plus-menu__title">
                  {{ t('newBradley.projectWorkspace.files.analyseData') }}
                </span>
                <span class="bradley-plus-menu__subtitle">
                  {{ t('newBradley.projectWorkspace.files.analyseDataSubtitle') }}
                </span>
              </span>
            </button>
          </template>

          <template v-else>
            <div class="bradley-plus-menu__picker-header">
              <button
                type="button"
                class="bradley-plus-menu__back"
                :aria-label="t('newBradley.projectWorkspace.files.backToMenu')"
                @click="backToMenu"
              >
                <i
                  class="pi pi-chevron-left"
                  aria-hidden="true"
                />
              </button>
              <span class="bradley-plus-menu__picker-title">
                {{ projectTitleUpper }}
              </span>
            </div>

            <div
              v-if="!sortedProjectFiles.length"
              class="bradley-plus-menu__empty"
            >
              {{ t('newBradley.projectWorkspace.files.pickerEmpty') }}
            </div>

            <div
              v-else
              class="bradley-plus-menu__picker-list"
            >
              <button
                v-for="file in sortedProjectFiles"
                :key="getBradleyProjectFileId(file)"
                type="button"
                class="bradley-plus-menu__file-row"
                :class="{ 'bradley-plus-menu__file-row--selected': isFileSelected(file) }"
                @click="toggleFileSelection(file, $event)"
              >
                <span
                  class="bradley-plus-menu__checkbox"
                  :class="{ 'bradley-plus-menu__checkbox--checked': isFileSelected(file) }"
                  aria-hidden="true"
                >
                  <i
                    v-if="isFileSelected(file)"
                    class="pi pi-check"
                  />
                </span>
                <span
                  class="bradley-plus-menu__file-icon"
                  aria-hidden="true"
                >
                  <i :class="getBradleyProjectFileIconClass(file)" />
                </span>
                <span class="bradley-plus-menu__file-copy">
                  <span class="bradley-plus-menu__file-name">
                    {{ getBradleyProjectFileName(file) }}
                  </span>
                  <span class="bradley-plus-menu__file-meta">
                    {{ formatUploadedLabel(file) }}
                  </span>
                </span>
              </button>
            </div>

            <div class="bradley-plus-menu__picker-footer">
              <button
                type="button"
                class="bradley-plus-menu__cancel"
                @click.stop="closeMenu"
              >
                {{ t('newBradley.deleteModal.cancel') }}
              </button>
              <button
                type="button"
                class="bradley-plus-menu__attach"
                :disabled="!selectedCount"
                @click="attachSelectedFiles"
              >
                {{ attachLabel }}
              </button>
            </div>
          </template>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<style scoped>
.bradley-plus-menu {
  position: relative;
}

.bradley-plus-menu__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  margin: 0;
  padding: 0;
  color: var(--color-dss-primary-500, #6366f1);
  background: transparent;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.bradley-plus-menu__trigger:hover {
  background: #f3f4f6;
}

.bradley-plus-menu__trigger i {
  font-size: 1rem;
}

.bradley-plus-menu__legacy-panel {
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 0;
  z-index: 50;
  width: 18rem;
  padding: 0.5rem 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  box-shadow: 0 12px 32px rgb(17 24 39 / 18%);
}

.bradley-plus-menu__legacy-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  margin: 0;
  padding: 0.75rem 1rem;
  color: #374151;
  font-family: var(--font-inter, Inter, sans-serif);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  letter-spacing: 0.005em;
  text-align: left;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.bradley-plus-menu__legacy-item:hover {
  background: #f9fafb;
}

.bradley-plus-menu__legacy-icon {
  flex-shrink: 0;
  color: #94a3b8;
  font-size: 1.125rem;
}
</style>

<!-- Teleported panel lives on body; keep styles unscoped. -->
<style>
.bradley-plus-menu__panel {
  padding: 0.5rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  box-shadow: 0 12px 32px rgb(17 24 39 / 12%);
}

.bradley-plus-menu__panel::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: 0.875rem;
  width: 12px;
  height: 12px;
  background: #fff;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  transform: rotate(45deg);
}

.bradley-plus-menu__panel--down::after {
  top: -6px;
  bottom: auto;
  border-right: none;
  border-bottom: none;
  border-left: 1px solid #e5e7eb;
  border-top: 1px solid #e5e7eb;
}

.bradley-plus-menu__panel--picker {
  padding: 0;
  overflow: hidden;
}

.bradley-plus-menu__panel--picker::after {
  display: none;
}

.bradley-plus-menu__item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  margin: 0;
  padding: 0.75rem;
  text-align: left;
  background: transparent;
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.bradley-plus-menu__item:hover {
  background: #f5f5ff;
}

.bradley-plus-menu__icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  color: var(--color-dss-primary-700, #4338ca);
  background: #ebebf9;
  border-radius: 0.625rem;
}

.bradley-plus-menu__icon i {
  font-size: 0.95rem;
}

.bradley-plus-menu__icon--analyse {
  color: #fff;
  background: var(--color-dss-primary-600, #4f46e5);
}

.bradley-plus-menu__copy {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.bradley-plus-menu__copy--grow {
  flex: 1 1 auto;
}

.bradley-plus-menu__title {
  color: #111827;
  font-family: var(--font-inter, Inter, sans-serif);
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.3;
}

.bradley-plus-menu__subtitle {
  color: #9ca3af;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.35;
}

.bradley-plus-menu__chevron {
  flex-shrink: 0;
  align-self: center;
  color: #9ca3af;
  font-size: 0.75rem;
}

.bradley-plus-menu__divider {
  height: 1px;
  margin: 0.25rem 0.5rem;
  background: #e5e7eb;
}

.bradley-plus-menu__picker-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1rem 0.5rem;
}

.bradley-plus-menu__back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  margin: 0;
  padding: 0;
  color: #6b7280;
  background: transparent;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
}

.bradley-plus-menu__back:hover {
  background: #f3f4f6;
}

.bradley-plus-menu__picker-title {
  color: #6b7280;
  font-family: var(--font-inter, Inter, sans-serif);
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1.3;
}

.bradley-plus-menu__empty {
  padding: 1.5rem 1rem;
  color: #9ca3af;
  font-size: 0.875rem;
  text-align: center;
}

.bradley-plus-menu__picker-list {
  display: flex;
  flex-direction: column;
  max-height: 16rem;
  overflow-y: auto;
  padding: 0.25rem 0.5rem;
}

.bradley-plus-menu__file-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  margin: 0;
  padding: 0.625rem 0.5rem;
  text-align: left;
  background: transparent;
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.bradley-plus-menu__file-row:hover,
.bradley-plus-menu__file-row--selected {
  background: #f5f5ff;
}

.bradley-plus-menu__checkbox {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  color: transparent;
  background: #fff;
  border: 1.5px solid #d1d5db;
  border-radius: 0.25rem;
}

.bradley-plus-menu__checkbox--checked {
  color: #fff;
  background: var(--color-dss-primary-600, #4f46e5);
  border-color: var(--color-dss-primary-600, #4f46e5);
}

.bradley-plus-menu__checkbox i {
  font-size: 0.625rem;
  font-weight: 700;
}

.bradley-plus-menu__file-icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  color: var(--color-dss-primary-700, #4338ca);
  background: #ebebf9;
  border-radius: 0.625rem;
}

.bradley-plus-menu__file-icon i {
  font-size: 0.95rem;
}

.bradley-plus-menu__file-copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.bradley-plus-menu__file-name {
  overflow: hidden;
  color: #111827;
  font-family: var(--font-inter, Inter, sans-serif);
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bradley-plus-menu__file-meta {
  color: #9ca3af;
  font-size: 0.75rem;
  line-height: 1.3;
}

.bradley-plus-menu__picker-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0.75rem 1rem 1rem;
  border-top: 1px solid #f3f4f6;
}

.bradley-plus-menu__cancel {
  margin: 0;
  padding: 0.5rem 0.25rem;
  color: var(--color-dss-primary-600, #4f46e5);
  font-size: 0.875rem;
  font-weight: 600;
  background: transparent;
  border: none;
  cursor: pointer;
}

.bradley-plus-menu__attach {
  margin: 0;
  padding: 0.5rem 0.875rem;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.3;
  background: var(--color-dss-primary-600, #4f46e5);
  border: none;
  border-radius: 0.625rem;
  cursor: pointer;
}

.bradley-plus-menu__attach:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.bradley-plus-menu__attach:hover:not(:disabled) {
  background: var(--color-dss-primary-700, #4338ca);
}

.bradley-plus-menu__transition-enter-active,
.bradley-plus-menu__transition-leave-active {
  transition: opacity 0.1s ease, transform 0.1s ease;
}

.bradley-plus-menu__transition-enter-from,
.bradley-plus-menu__transition-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.bradley-plus-menu__transition-enter-to,
.bradley-plus-menu__transition-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>
